import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { MuiTelInput } from 'mui-tel-input';
import { routes } from '../../constants';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import api from '../../api';
import StyledPage from '../../components/StyledPage';

export default function Register() {
  const defaultForm = {
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    organization_name: '',
    phone_number: '',
  };

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [userData, setUserData] = useState(defaultForm);
  const [inputErrors, setInputErrors] = useState({});
  const [successfulRegister, setSuccessfulRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState('CA');

  useEffect(() => {
    async function fetchCountry() {
      try {
        const data = await api.getCountryCode();
        if (data.country_code) {
          setDefaultCountry(data.country_code);
        }
      } catch (error) {
        console.error('Error fetching country:', error);
      }
    }
    fetchCountry();
  }, []);

  const modifyFormData = (property, event) => {
    const dataCopy = { ...userData };
    dataCopy[property] = event.target ? event.target.value : event;

    const errorsCopy = { ...inputErrors };
    delete errorsCopy[property];

    setUserData(dataCopy);
    setInputErrors(errorsCopy);
  };

  const register = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const registerStatus = await api.registerUser(userData);

      if (registerStatus.status !== 201) {
        setInputErrors(registerStatus.data);
      } else {
        setSuccessfulRegister(true);
      }
    } catch (error) {
      setInputErrors({ error: 'Registration failed. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledPage>
      <Box sx={{ width: '100%' }}>
        {successfulRegister ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ mb: 2, color: '#00438E', fontWeight: 'bold' }}>
              <FormattedMessage id="register.success_header" />
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              <FormattedMessage id="register.success_message" />
            </Typography>
          </Box>
        ) : (
          <>
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <Box
                  component="img"
                  src="/logo.png"
                  alt="Prompta.AI"
                  sx={{ height: 40, width: 'auto' }}
                />
              </Box>
            )}
            <Typography
              variant="h5"
              sx={{
                mb: 1,
                color: '#00438E',
                textAlign: 'center',
                fontWeight: 700,
              }}
            >
              <FormattedMessage id="register.register" />
            </Typography>
            <Typography
              variant="body2"
              sx={{ mb: 4, color: '#6B7280', textAlign: 'center' }}
            >
              <FormattedMessage id="register.already_registered" />{' '}
              <Link
                onClick={() => navigate(routes.login)}
                sx={{
                  color: '#00438E',
                  fontWeight: 600,
                  '&:hover': { color: '#4A87C8' },
                  cursor: 'pointer',
                }}
              >
                <FormattedMessage id="login.sign_in" />
              </Link>
            </Typography>
            <Box component="form" onSubmit={register}>
              <Grid container spacing={2}>
                {/* Username */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    error={Object.keys(inputErrors).includes('username')}
                    helperText={inputErrors['username'] || ''}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    type="text"
                    label={<FormattedMessage id="register.username_r" />}
                    value={userData.username}
                    onChange={(event) => modifyFormData('username', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>

                {/* Email */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    error={Object.keys(inputErrors).includes('email')}
                    helperText={inputErrors['email'] || ''}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    type="text"
                    label={<FormattedMessage id="register.email" />}
                    value={userData.email}
                    onChange={(event) => modifyFormData('email', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>

                {/* Password */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    error={Object.keys(inputErrors).includes('password')}
                    helperText={inputErrors['password'] || ''}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    type="password"
                    label={<FormattedMessage id="register.password_r" />}
                    value={userData.password}
                    onChange={(event) => modifyFormData('password', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>

                {/* First Name and Last Name */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    type="text"
                    label={<FormattedMessage id="register.first_name" />}
                    value={userData.first_name}
                    onChange={(event) => modifyFormData('first_name', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    type="text"
                    label={<FormattedMessage id="register.last_name" />}
                    value={userData.last_name}
                    onChange={(event) => modifyFormData('last_name', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>

                {/* Organization Name */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    error={Object.keys(inputErrors).includes('organization_name')}
                    helperText={inputErrors['organization_name'] || ''}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    label={<FormattedMessage id="register.organization_name_r" />}
                    value={userData.organization_name}
                    onChange={(event) => modifyFormData('organization_name', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>

                {/* Phone Number */}
                <Grid item xs={12}>
                  <MuiTelInput
                    fullWidth
                    defaultCountry={defaultCountry}
                    error={Object.keys(inputErrors).includes('phone_number')}
                    helperText={inputErrors['phone_number'] || ''}
                    slotProps={{ inputLabel: { shrink: true } }}
                    size="small"
                    label={<FormattedMessage id="register.phone_number" />}
                    value={userData.phone_number}
                    onChange={(event) => modifyFormData('phone_number', event)}
                    sx={{
                      '& .MuiInputBase-root': { backgroundColor: '#f0f0f0', borderRadius: 1 },
                      '& .MuiInputLabel-root': { color: '#00438E' },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#00438E' },
                        '&:hover fieldset': { borderColor: '#4A87C8' },
                        '&.Mui-focused fieldset': { borderColor: '#4A87C8' },
                      },
                    }}
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Button
                    size="large"
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      width: '100%',
                      backgroundColor: '#00438E',
                      '&:hover': { backgroundColor: '#003A75' },
                      py: 1.5,
                      mt: 1,
                    }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : <FormattedMessage id="register.signup" />}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </>
        )}
      </Box>
    </StyledPage>
  );
}
